import HourglassTopTwoToneIcon from "@mui/icons-material/HourglassTopTwoTone";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import { Alert, type AlertColor, Box, Snackbar, styled, useTheme } from "@mui/material";

import { createLogger } from "@/logger";

const log = createLogger("upload");

import * as Sentry from "@sentry/browser";
import Sessioncore from "@storyboard-fm/sessioncore";
import Bowser from "bowser";
import cuid from "cuid";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTus } from "use-tus";

import SendIcon from "@/components/Icons/Send";
import { client } from "@/config";
import useLocationChange from "@/hooks/useLocationChange";
import { AppContext } from "@/models/AppStateProvider";
import { AudioAppContext } from "@/models/AudioAppContextProvider";
import { publishToWorkspaceFeed } from "@/models/actions/clientActions";
import { DataContext } from "@/models/DataProvider";
import { FeedContext } from "@/models/FeedContextProvider";
import { CurrentFeedContext } from "@/models/StateProviders/currentFeedProvider";
import { MyAccountContext } from "@/models/StateProviders/myAccountProvider";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider";
import { TrackingContext } from "@/models/TrackingStateProvider";
import { UxContext } from "@/models/UxStateProvider";
import { buildVersion, transformWorkspaceMembershipAvailabilityRecord } from "@/utils";
import { AUDIO_FILTERS, getBandpassData } from "@/utils/audio";
import type { AudioNodeInterface } from "@/utils/recording";
import {
  type AudioContextInterface,
  cancelWakeLock,
  clearIndexDB,
  fetchAudioMetaData,
  fetchUploadConfig,
  forceFileDownload,
  getPaddedTime,
  isWavPcmDurationMismatch,
  monitorAudioLevels,
  readWavPcmFormat,
  requestWakeLock,
  setupAudioNode,
} from "@/utils/recording";
import TTSForm from "../ModalForms/TTSForm";
import { drawerOpenStyles } from "../SBAppBar";
import "./index.css";
import RecordingRequestHandler from "@/components/electron/RecordingRequestHandler.tsx";
import MakeCall from "@/components/MakeCall";
import RecordingButton from "@/components/RecordingButton";

const showSnackBar: boolean = false;
const sessionCorePrefix: string = "sessioncore-";
const maxRecordingLength: number = Number(import.meta.env.VITE_SESSION_RECORDING_LENGTH ?? 900);
const intervalLength: number = Number(import.meta.env.VITE_SESSION_RECORDING_INTERVAL ?? 90);

interface SessionCoreInterface {
  id: string;
  ctx: AudioContext;
  source: MediaStreamAudioSourceNode;
  setup: () => void;
  start: () => void;
  stop: () => void;
  wav: () => Blob;
  blob: () => Blob;
  on: (event: string, listener: (...args: unknown[]) => void) => void;
}

interface IntervalInterface {
  message: string;
  color: string;
  severity: AlertColor;
}

interface TimeInterface {
  milliseconds: number;
  seconds: number;
  time: string;
}

const initialTimeObject = {
  milliseconds: 0,
  seconds: 0,
  time: "00:00",
};

/**
 * Interval Steps. Automatically set when interval changes
 */
const intervals: IntervalInterface[] = [
  { message: "", color: "#00ff04", severity: "info" },
  {
    message: "Shorter messages are easier to consume",
    color: "#00ff04",
    severity: "info",
  },
  {
    message: "Consider recording multiple short messages rather than one long one",
    color: "#84ff00",
    severity: "info",
  },
  { message: "", color: "#c8ff00", severity: "success" },
  { message: "", color: "#fffb00", severity: "success" },
  { message: "", color: "#FFC400FF", severity: "warning" },
  { message: "", color: "#ff9900", severity: "warning" },
  { message: "", color: "#ff7300", severity: "warning" },
  { message: "", color: "#ff4400", severity: "error" },
  {
    message: `The recording limit is ${maxRecordingLength / 60} min`,
    color: "#ff1e00",
    severity: "error",
  },
  { message: "", color: "#ff0073", severity: "error" },
];

const radialColors = [
  "#00ff04",
  "#84ff0",
  "#c8ff00",
  "#fffb00",
  "#FFC400FF",
  "#ff9900",
  "#ff7300",
  "#ff4400",
  "#ff1e00",
  "#ff0073",
];

const SessionRecorderWrapper = styled("div")({});

export default function SessionRecorderFooter({
  workspaceId,
  feedId,
  aliasOwnerAccountId,
}: {
  workspaceId: string;
  feedId: string;
  aliasOwnerAccountId?: string;
}) {
  const { ampli } = React.useContext(TrackingContext);
  const { config, flags } = useContext(AppContext);
  const { preferredLanguage } = useContext(DataContext);
  const { currentFeed } = useContext(CurrentFeedContext);
  const { currentWorkspaceId } = useContext(WorkspaceContext);
  const { myAccount } = useContext(MyAccountContext);
  const { playerPlaying, pauseItem, activeItemId } = React.useContext(AudioAppContext);
  const { vListRef, scrollToBottomOfFeed, items } = useContext(FeedContext);
  const { accountMap } = useContext(WorkspaceContext);
  const accountRecordWithStatus = accountMap?.get(aliasOwnerAccountId);

  const transformedAccount = useMemo(() => {
    return accountRecordWithStatus?.id ? transformWorkspaceMembershipAvailabilityRecord(accountRecordWithStatus) : null;
  }, [accountRecordWithStatus]);

  const { newDesign, allowDispatcherCalls } = flags;
  const forceDownload: boolean = flags.recordingDebug ?? false;

  const theme = useTheme();
  const { isSmUp, leftNavOpen, rightNavOpen } = useContext(UxContext);

  const { upload, setUpload } = useTus();
  const { platform, browser } = Bowser.parse(window.navigator.userAgent);
  const isFirefox = browser?.name?.toLowerCase()?.includes("firefox");
  const isMobile: boolean = platform.type === "mobile";

  /**
   * useRef
   */
  const requestRef = useRef<number | undefined>(undefined);
  const sessionCore = useRef<SessionCoreInterface>({} as SessionCoreInterface);
  const startTime = useRef<Date>(new Date());
  const queuePlayingRef = useRef<boolean>(playerPlaying);
  // Set inside startRecording() so the stop flow can check whether the
  // active sessioncore emitted any append/write errors during recording.
  const sessionWriteFailedRef = useRef<() => boolean>(() => false);

  /**
   * useState
   */
  const [audioLevel, setAudioLevel] = useState<number>(0);
  const [audioNode, setAudioNode] = useState(null as AudioNodeInterface);
  const [color, setColor] = useState<string>("");
  const [counter, setCounter] = useState<number>(0);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [error, setError] = useState<object | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [processing, setProcessing] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [recording, setRecording] = useState<boolean>(false);
  const [sessionName, setSessionName] = useState<string>(`${sessionCorePrefix}${Date.now()}`);
  const [severity, setSeverity] = React.useState<AlertColor>("info");
  const [snackBarOpen, setSnackBarOpen] = React.useState<boolean>(false);
  const [timeObject, setTimeObject] = useState<TimeInterface>(initialTimeObject);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [wakeLock, setWakeLock] = useState(null);
  const [contentId, setContentId] = useState<string>(null);

  /**
   * Button disabled state
   */
  const disabledState = useMemo(() => {
    if (disabled) {
      return true;
    }
    if (error) {
      return false;
    }
    if (processing) {
      return true;
    }
    if (uploadProgress > 0) {
      return true;
    }
    return false;
  }, [processing, uploadProgress, error, disabled]);

  const markUploadAsComplete = () => {
    setProcessing(() => false);
    setContentId(() => null);
    setUploadProgress(() => 0);
    setCounter(() => 0);
    setDisabled(() => false);
    clearIndexDB(sessionName, null).then((data) => data);
  };

  const focusToTTSForm = () => {
    const input = document.getElementById("tts-form-text-input");
    if (input) {
      setTimeout(() => input.focus(), 50);
    }
  };

  /**
   * Handle File Upload
   * @param file
   * @param duration
   */
  const setupUploadData = async (file: Blob, duration: number) => {
    const { stage, tusUrl } = fetchUploadConfig(config);
    const uploadContentId = contentId || cuid();

    const item = await publishToWorkspaceFeed({
      workspaceId,
      feedId,
      contentId: uploadContentId,
      isSilent: currentFeed?.isSilent ? true : false,
    });

    const itemId = item?.id;
    client
      .createContentEvent({
        contentId: uploadContentId,
        step: "client_uploading",
        status: "started",
        feedId,
        workspaceId: currentWorkspaceId,
      })
      .then(() => {});
    const startTime = Date.now();

    const audioMetaData: AudioContextInterface = fetchAudioMetaData(sessionCore.current.ctx);

    const metadata = {
      filename: uploadContentId,
      filetype: file.type,
      id: uploadContentId,
      codec: "wav",
      type: "audio",
      uploadId: uploadContentId,
      surface: "web",
      surfaceContext: window?.navigator?.userAgent,
      surfaceBuild: buildVersion(),
      stage: stage,
      feedItemId: itemId,
      feedId: feedId,
      accountId: myAccount?.id,
      duration: duration ? duration.toString() : "",
      preferredLanguage,
      inputType: "Recording",
      ...audioMetaData,
    };

    setUpload(file, {
      storeFingerprintForResuming: true,
      endpoint: tusUrl,
      retryDelays: [0, 3000, 5000, 10000, 20000],
      metadata,
      onChunkComplete: (chunkSize, bytesAccepted, bytesTotal) => {
        log.debug("onChunkComplete", { chunkSize, bytesAccepted, bytesTotal });
      },
      onProgress: (bytesUploaded, bytesTotal) => {
        setUploadProgress(() => 1);
        if (bytesUploaded > 0) {
          const percentage = Math.floor((bytesUploaded / bytesTotal) * 100);
          setUploadProgress(() => percentage);
        }
      },
      onError: async (error) => {
        console.error("There wan an error:", error);
        ampli.uploadFailed({ feedId });
        setUploadProgress(() => 0);
        setProcessing(() => false);
        setDisabled(() => false);
        setError(() => error);
        Sentry.captureException(error);
        client
          .createContentEvent({
            workspaceId: currentWorkspaceId,
            contentId: uploadContentId,
            step: "client_uploading",
            status: "failed",
            feedId,
            context: {
              milliseconds: Date.now() - startTime,
            },
            error,
          })
          .then(() => {});
      },
      onSuccess: async () => {
        log.debug("File Upload Complete");
        client
          .createContentEvent({
            workspaceId: currentWorkspaceId,
            contentId: uploadContentId,
            step: "client_uploading",
            status: "finished",
            feedId,
            context: {
              milliseconds: Date.now() - startTime,
            },
          })
          .then(() => {});
        setError(() => null);
        markUploadAsComplete();
        ampli.uploadRecord();
      },
      onShouldRetry: (err, retryAttempt, options) => {
        log.error("Upload retry", err, retryAttempt, options);
        Sentry.captureException(err);
        return true;
      },
    });
  };

  /**
   * Process time with animationFrame
   * Builds a time object
   * milliseconds, seconds and time
   */
  const buildTimeObject = () => {
    requestRef.current = requestAnimationFrame(buildTimeObject);
    const now = new Date().getTime();
    const then = startTime.current.getTime();
    const timeFloorAbs = Math.floor(Math.abs(then - now) / 1000);
    setTimeObject(() => ({
      milliseconds: Math.floor(Math.abs(then - now)),
      seconds: timeFloorAbs,
      time: getPaddedTime(timeFloorAbs),
    }));
  };

  /**
   * Start Timer
   */
  const startTimer = (ctx: AudioContext) => {
    startTime.current = new Date();
    cancelAnimationFrame(requestRef.current);
    requestRef.current = requestAnimationFrame(buildTimeObject);
  };

  /**
   * Stop timer
   */
  const stopTimer = () => {
    setTimeObject(() => initialTimeObject);
    cancelAnimationFrame(requestRef.current);
  };

  const [firefoxTimeout, setFirefoxTimeout] = useState<boolean>(false);

  /**
   * Start recording
   * Setup Session Core
   * Setup Audio Node to monitor audio levels
   * Start Timer
   */
  const startRecording = async () => {
    setError(() => null);

    if (isFirefox) {
      setFirefoxTimeout(() => true);
    }

    const localSessionName = cuid();
    setContentId(() => localSessionName);
    setSessionName(() => localSessionName);
    client
      .createContentEvent({
        workspaceId: currentWorkspaceId,
        contentId: localSessionName,
        step: "client_recording",
        status: "started",
        feedId,
      })
      .then(() => {});
    const session = new Sessioncore(localSessionName);
    // Sessioncore emits 'error' when hypercore.append() fails mid-recording
    // (e.g. IndexedDB quota exhaustion or a transient write failure). The
    // library silently drops the failed batch, so without a listener long
    // recordings can end up truncated in the field with no signal (WEB-1112).
    // Surface the first failure to Sentry/Amplitude and to the user so we can
    // diagnose and the recording isn't trusted blindly.
    let sessionWriteFailed = false;
    session.on("error", (err) => {
      const reportedError = err instanceof Error ? err : new Error(String(err));
      log.error("Sessioncore emitted error during recording", reportedError);
      if (!sessionWriteFailed) {
        sessionWriteFailed = true;
        Sentry.captureException(reportedError, {
          extra: { contentId: localSessionName },
          tags: { feature: "voice-recording", step: "sessioncore-error" },
        });
        ampli.localProcessingError({
          duration: 0,
          expectedDuration: 0,
          message: `sessioncore error during recording: ${reportedError.message}`,
        });
      }
    });
    sessionWriteFailedRef.current = () => sessionWriteFailed;
    const f1 = AUDIO_FILTERS.STANDARD.HIGHPASS;
    const f2 = AUDIO_FILTERS.STANDARD.LOWPASS;
    const { centerFreq, q } = getBandpassData(f1, f2);
    await session.setup(null, { bandpass: [centerFreq, q] });
    const ctx = session.ctx;
    await session.start();
    const localAudioNode = await setupAudioNode();
    if (localAudioNode) {
      setAudioNode(() => localAudioNode);
    } else {
      setAudioNode(() => null);
    }
    const wakeLock = await requestWakeLock();

    /**
     * Conditionally wait for firefox to load sessioncore, since it's not instant
     * 2 seconds seems to be a long enough time to wait for it to load
     */
    if (isFirefox) {
      await new Promise((resolve) => setTimeout(resolve, 2200));
      setFirefoxTimeout(() => false);
    }

    // set the previous playback state before we pause the recording
    queuePlayingRef.current = playerPlaying;
    if (playerPlaying) {
      pauseItem();
    }
    setWakeLock(() => wakeLock);
    setRecording(() => true);
    startTimer(ctx);
    ampli.startRecord({ feedId });
    sessionCore.current = session;
  };

  const compareTimerDurationWithSessionCoreDuration = (duration: number, sessionCoreDuration: number) => {
    // how long the timer says the audio is
    const loggedDuration: number = Math.ceil(duration);
    // how long session core says the audio is
    const sessionDuration: number = Math.floor(sessionCoreDuration);
    // Allow ±10% (with a 10s floor) of the timer-measured duration to account for
    // small encoder / scheduling skew. Anything outside that window indicates the
    // produced audio was truncated or extended relative to what the user recorded.
    const allowableRange: number = Math.max(loggedDuration / 10, 10);
    const low: number = loggedDuration - allowableRange;
    const high: number = loggedDuration + allowableRange;
    const durationMisMatch: boolean = sessionDuration < low || sessionDuration > high;

    if (durationMisMatch) {
      ampli.localProcessingError({
        duration: loggedDuration,
        expectedDuration: sessionDuration,
        message: "sessioncore audio duration does not match timer duration",
      });
    }
  };

  /**
   * Stop recording and upload audio file
   */
  const stopRecording = async () => {
    setFirefoxTimeout(() => false);
    const duration = timeObject.milliseconds / 1000;
    // prevent double clicks
    if (disabled) {
      return false;
    }
    client
      .createContentEvent({
        contentId: contentId,
        step: "client_recording",
        status: "finished",
        feedId,
        workspaceId: currentWorkspaceId,
        context: {
          milliseconds: timeObject.milliseconds,
        },
      })
      .then(() => {});
    setDisabled(() => true);
    setProcessing(() => true);
    stopTimer();
    const startProcessingTime = Date.now();
    client
      .createContentEvent({
        contentId: contentId,
        step: "client_processing",
        status: "started",
        feedId,
        workspaceId: currentWorkspaceId,
      })
      .then(() => {});
    // wait 1/2 extra second to ensure all recording is complete.
    await new Promise((resolve) => setTimeout(resolve, 500));
    await sessionCore.current.stop();
    setAudioLevel(() => 0);
    setCounter(() => 0);
    setRecording(() => false);
    setSnackBarOpen(() => false);
    focusToTTSForm();

    let file: Blob | null = null;
    try {
      file = await sessionCore.current.wav();
    } catch (e) {
      log.error("Failed to encode WAV from sessioncore", e);
      Sentry.captureException(e, {
        extra: { contentId, durationSeconds: duration },
        tags: { feature: "voice-recording", step: "encode-wav" },
      });
    }

    if (!file) {
      ampli.audioProcessingTimeout();
    }

    let processingDuration = 0;
    try {
      compareTimerDurationWithSessionCoreDuration(duration, sessionCore.current.ctx?.currentTime ?? duration);

      if (file && forceDownload) {
        forceFileDownload(file, `${sessionName}.wav`, "audio/x-wav");
      }

      processingDuration = Date.now() - startProcessingTime;
      const writeFailedDuringRecording = sessionWriteFailedRef.current();
      if (file) {
        const wavFormat = await readWavPcmFormat(file);
        let expectedBytes: number | undefined;
        let sizeMismatch = false;
        if (wavFormat) {
          const wavDurationCheck = isWavPcmDurationMismatch(duration, wavFormat);
          sizeMismatch = wavDurationCheck.mismatch;
          expectedBytes = 44 + wavDurationCheck.expectedPcmBytes;
          if (sizeMismatch) {
            ampli.localProcessingError({
              duration: Math.ceil(duration),
              expectedDuration: Math.round(wavDurationCheck.pcmDurationSeconds),
              message: "wav file size does not match recorded duration",
            });
          }
        }
        // If sessioncore had a write error or the wav size doesn't match the
        // recorded duration, bail out instead of uploading a truncated file.
        if (writeFailedDuringRecording || sizeMismatch) {
          setProcessing(() => false);
          setDisabled(() => false);
          setError(
            () => new Error("Recording may be incomplete due to a browser storage error. Please try sending it again."),
          );
          client
            .createContentEvent({
              contentId: contentId,
              step: "client_processing",
              status: "failed",
              feedId,
              workspaceId: currentWorkspaceId,
              context: {
                milliseconds: processingDuration,
                fileBytes: file.size,
                expectedBytes,
                writeFailedDuringRecording,
                sizeMismatch,
              },
            })
            .then(() => {});
        } else {
          client
            .createContentEvent({
              contentId: contentId,
              step: "client_processing",
              status: "finished",
              feedId,
              workspaceId: currentWorkspaceId,
              context: {
                milliseconds: processingDuration,
                fileBytes: file.size,
                expectedBytes,
              },
            })
            .then(() => {});
          await setupUploadData(file, duration);
        }
      } else {
        // No file produced — surface the failure to the user instead of leaving
        // them stuck on a spinning hourglass forever (see WEB-1112).
        setProcessing(() => false);
        setDisabled(() => false);
        setError(() => new Error("Failed to process recording. Please try again."));
        client
          .createContentEvent({
            contentId: contentId,
            step: "client_processing",
            status: "failed",
            feedId,
            workspaceId: currentWorkspaceId,
            context: {
              milliseconds: processingDuration,
            },
          })
          .then(() => {});
      }
    } catch (e) {
      log.error("Failed during recording post-processing", e);
      Sentry.captureException(e, {
        extra: { contentId, durationSeconds: duration },
        tags: { feature: "voice-recording", step: "post-encode" },
      });
      processingDuration = Date.now() - startProcessingTime;
      setProcessing(() => false);
      setDisabled(() => false);
      setError(() => new Error("Failed to process recording. Please try again."));
      client
        .createContentEvent({
          contentId: contentId,
          step: "client_processing",
          status: "failed",
          feedId,
          workspaceId: currentWorkspaceId,
          context: {
            milliseconds: processingDuration,
          },
        })
        .then(() => {});
    } finally {
      // close/stop any tracks for force stop recording
      audioNode?.mediaStream?.getTracks().forEach((track) => track.stop());
      await cancelWakeLock(wakeLock);
    }
    scrollToBottomOfFeed();
  };

  /**
   * Cancel recording and clear default values
   */
  const cancel = async () => {
    client
      .createContentEvent({
        contentId: contentId,
        step: "client_recording",
        status: "canceled",
        feedId,
        workspaceId: currentWorkspaceId,
        context: {
          milliseconds: timeObject.milliseconds,
        },
      })
      .then(() => {});
    stopTimer();
    setContentId(() => null);
    setAudioLevel(() => 0);
    setCounter(() => 0);
    setProcessing(() => false);
    // small delay for animations
    setTimeout(() => {
      setRecording(() => false);
      focusToTTSForm();
    }, 300);
    setSnackBarOpen(() => false);
    await sessionCore.current.stop();
    audioNode.mediaStream.getTracks().forEach((track) => track.stop());
    await cancelWakeLock(wakeLock);
  };

  /**
   * Set the current interval / counter level
   * @param seconds
   */
  const setCurrentCounter = useCallback(
    (seconds: number) => setCounter(() => Math.floor(seconds / intervalLength)),
    [],
  );

  /**
   * Create a smooth animation for the progress bar
   * @param milliseconds
   */
  const calculateRadialProgressInMilliSeconds = useCallback(
    (milliseconds: number) => {
      const seconds = milliseconds / 1000;
      const multiplier = counter * 100;
      const radialProgressPercentage = Number((Math.ceil(seconds * 100) / intervalLength - multiplier).toFixed(2));
      if (radialProgressPercentage > 100) {
        setProgress(() => 0);
      } else {
        setProgress(() => radialProgressPercentage);
      }
    },
    [counter],
  );

  /**
   * Auto-Stop recording if seconds is greater or eaual to max recording length
   * @param milliseconds
   */
  const stopRecordingRef = useRef(stopRecording);
  stopRecordingRef.current = stopRecording;

  const autoStopRecording = useCallback(
    async (milliseconds: number) => {
      const maxMilliseconds = maxRecordingLength * 1000;
      if (milliseconds >= maxMilliseconds && !disabled) {
        await stopRecordingRef.current();
      }
    },
    [disabled],
  );

  /**
   * Close The Snackbar
   */
  const handleClose = () => setSnackBarOpen(false);

  /**
   * Open Snackbar when a message is available
   * @param counter
   */
  const openSnackBar = useCallback((counterValue: number) => {
    const currentInterval = intervals[counterValue];
    if (!currentInterval) {
      return false;
    }
    const { message, color, severity } = currentInterval;
    if (color) {
      setColor(() => color);
    }

    const hasMessage = message?.length > 1 ? message : false;
    if (hasMessage) {
      setMessage(() => message);
      setSnackBarOpen(() => true);
    }
    if (severity) {
      setSeverity(() => severity);
    }
  }, []);

  const snackBarOpenRef = useRef(snackBarOpen);
  snackBarOpenRef.current = snackBarOpen;

  /**
   * Close Snackbar
   * @param milliseconds
   */
  const closeSnackBar = useCallback(
    (milliseconds: number) => {
      const seconds = Math.floor(milliseconds / 1000);
      const currentInterval = (seconds + 2) % intervalLength === 1 && seconds + 2 >= intervalLength;

      if (currentInterval && snackBarOpenRef.current) {
        setSnackBarOpen(() => false);
      }
    },
    [], // no longer depends on snackBarOpen
  );

  /**
   * Upload file when it's ready
   */
  useEffect(() => {
    if (upload) {
      setProcessing(() => false);
      upload.start();
    }
  }, [upload]);

  /**
   * Open Snack Bar when messages are available
   */
  useEffect(() => {
    openSnackBar(counter);
  }, [counter, openSnackBar]);

  /**
   * Close Snackbar
   * Auto Stop Recording
   * Calculate Radial Progress
   * Set Audio Levels
   */
  useEffect(() => {
    closeSnackBar(timeObject.milliseconds);
    autoStopRecording(timeObject.milliseconds);
    calculateRadialProgressInMilliSeconds(timeObject.milliseconds);
    setAudioLevel(() => monitorAudioLevels(audioNode, isMobile));
  }, [
    timeObject.milliseconds,
    closeSnackBar,
    autoStopRecording,
    calculateRadialProgressInMilliSeconds,
    audioNode,
    isMobile,
  ]);

  /**
   * Update counter based on current time in seconds
   */
  useEffect(() => {
    setCurrentCounter(timeObject.seconds || 0);
  }, [timeObject.seconds, setCurrentCounter]);

  /**
   * On Mount clear index db
   */
  useEffect(() => {
    clearIndexDB(null, sessionCorePrefix);
  }, []);

  const goToPlaybackItem = React.useCallback(() => {
    const currentQueueItemItemId = activeItemId;
    if (currentQueueItemItemId) {
      const queueItemIndex = items.findIndex((item) => item.id === currentQueueItemItemId);
      vListRef?.current?.scrollToIndex(queueItemIndex, {
        align: "start",
        smooth: true,
      });
    }
  }, [items, vListRef, activeItemId]);

  // if a user leaves the current feed and is recording, then cancel their recording
  useLocationChange(() => {
    if (recording) {
      cancel();
    }
  });

  // Current active icon in button
  const buttonIcon = useMemo(() => {
    if (processing) {
      return <HourglassTopTwoToneIcon className="processing-request" sx={{ fontSize: 45 }} />;
    }
    if (recording) {
      return <SendIcon />;
    }
    return <MicIcon sx={{ fontSize: 55 }} />;
  }, [recording, processing]);

  // Bottom text on footer
  const bottomMessage = useMemo(() => {
    if (processing) {
      return "Processing Request";
    }
    if (uploadProgress > 0) {
      return "Uploading";
    }
    return `Click to ${recording ? "send" : "record"}`;
  }, [processing, uploadProgress, recording]);

  const showAudioLevels = useMemo(
    () => !!(recording && audioLevel && !disabled && timeObject.milliseconds > 0),
    [recording, audioLevel, disabled, timeObject],
  );

  const _drawerOpenStyles = {
    ...drawerOpenStyles({
      isSmUp,
      leftNavOpen,
      rightNavOpen,
      theme,
    }),
  };

  const noAudioDetected = recording && audioLevel === 0 && timeObject.milliseconds > 250;

  const showMakeCallButton = useMemo(
    () => allowDispatcherCalls && aliasOwnerAccountId,
    [allowDispatcherCalls, aliasOwnerAccountId],
  );

  if (!workspaceId || !feedId) return null;

  const recordingErrorSnackbar = error ? (
    <Snackbar
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      sx={{
        width: "100%",
        px: 2,
        py: 1,
        top: { xs: 100 },
        left: { xs: 0 },
        transform: { xs: "none" },
        zIndex: 1100,
        ..._drawerOpenStyles,
      }}
      open={true}
      autoHideDuration={6000}
      onClose={() => setError(() => null)}
    >
      <Alert
        severity="error"
        onClose={() => setError(() => null)}
        sx={{ width: "100%", maxWidth: 425 }}
        elevation={6}
        variant="filled"
      >
        {(error as Error)?.message || "Failed to send recording. Please try again."}
      </Alert>
    </Snackbar>
  ) : null;

  if (newDesign) {
    return (
      <>
        {recordingErrorSnackbar}
        <TTSForm
          makeCallButton={
            showMakeCallButton ? (
              <MakeCall
                disabled={recording}
                feedId={feedId}
                toAccountId={myAccount.id}
                fromAccountId={aliasOwnerAccountId}
                name={accountRecordWithStatus?.name}
                handsFreeEnabled={accountRecordWithStatus?.mostRecentHandsFreeStatusEnabled}
                offline={transformedAccount?.offline}
              />
            ) : null
          }
          recordingProps={{
            progress,
            processing,
            audioLevel,
            showAudioLevels,
            time: timeObject.time,
            disabled: disabledState,
            uploading: processing || uploadProgress > 0,
            color,
            uploadProgress,
          }}
          cancelRecording={() => cancel()}
          saveRecording={() => stopRecording()}
          makeRecording={
            <RecordingRequestHandler>
              <RecordingButton
                newDesign={true}
                time={timeObject.time}
                showAudioLevels={showAudioLevels}
                audioLevel={audioLevel}
                processing={processing}
                progress={progress}
                color={color}
                width={50}
                uploading={processing || uploadProgress > 0}
                disabled={disabledState}
                toggleRecord={recording ? stopRecording : startRecording}
                cancelRecord={() => {}}
              />
            </RecordingRequestHandler>
          }
          disableControls={recording}
        />
      </>
    );
  }

  return (
    <>
      <Box className="session-recording-footer-container" id="sessionRecorderFooterContainer">
        {(processing || uploadProgress > 0) && !error && (
          <Snackbar
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            sx={{
              width: "100%",
              px: 2,
              py: 1,
              top: { xs: 100 },
              left: { xs: 0 },
              transform: { xs: "none" },
              zIndex: 1100,
              ..._drawerOpenStyles,
            }}
            open={true}
          >
            <Alert
              icon={false}
              severity="info"
              sx={{
                width: "100%",
                color: theme.palette.primary.main,
                maxWidth: 425,
              }}
              elevation={6}
              variant="filled"
            >
              {processing && uploadProgress === 0 ? "Processing audio..." : `Sending message... ${uploadProgress}%`}
            </Alert>
          </Snackbar>
        )}
        {recordingErrorSnackbar}
        {recording && showSnackBar && (
          <Snackbar
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            sx={{
              width: "100%",
              px: 2,
              py: 1,
              top: { xs: 100 },
              left: { xs: 0 },
              transform: { xs: "none" },
              ..._drawerOpenStyles,
            }}
            open={snackBarOpen}
          >
            <Alert
              icon={false}
              onClose={handleClose}
              severity={severity}
              sx={{
                width: "100%",
                color: theme.palette.primary.main,
                maxWidth: 1300,
                ".MuiAlert-message": {
                  marginLeft: "auto",
                },
              }}
              elevation={6}
              variant="filled"
            >
              {message}
            </Alert>
          </Snackbar>
        )}
        <SessionRecorderWrapper
          className="session-recording-footer"
          aria-label={bottomMessage}
          sx={{
            ..._drawerOpenStyles,
          }}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              gap: showMakeCallButton ? 2 : 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <RecordingRequestHandler>
                <RecordingButton
                  time={timeObject.time}
                  showAudioLevels={showAudioLevels}
                  audioLevel={audioLevel}
                  processing={processing}
                  progress={progress}
                  color={color}
                  width={50}
                  uploading={processing || uploadProgress > 0}
                  disabled={disabledState}
                  toggleRecord={recording ? stopRecording : startRecording}
                  cancelRecord={() => cancel()}
                />
              </RecordingRequestHandler>
              {showMakeCallButton && (
                <MakeCall
                  disabled={recording}
                  feedId={feedId}
                  toAccountId={myAccount.id}
                  fromAccountId={aliasOwnerAccountId}
                  name={accountRecordWithStatus?.name}
                  handsFreeEnabled={accountRecordWithStatus?.mostRecentHandsFreeStatusEnabled}
                  offline={transformedAccount?.offline}
                />
              )}
            </Box>
            <Box
              sx={{
                flexGrow: 1,
                boxShadow: noAudioDetected ? `0 0 0 2px ${theme.palette.error.main}` : "none",
                borderRadius: 6,
                mb: 1,
                background: noAudioDetected ? theme.palette.error.main : "transparent",
              }}
            >
              {noAudioDetected && (
                <Box
                  sx={{
                    pl: 2,
                    fontSize: "18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: "flex" }}>
                    <MicOffIcon />
                  </Box>
                  <Box>No Audio Signal Detected. Please check your microphone.</Box>
                </Box>
              )}
              <TTSForm disableControls={recording} />
            </Box>
          </Box>
        </SessionRecorderWrapper>
        {recording && <div className="overlay" />}
      </Box>
    </>
  );
}
